import {
  Alert,
  Button,
  FileInput,
  Flex,
  Group,
  List,
  Modal,
  Progress,
  Select,
  Text,
} from '@mantine/core';
import { IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { BulkUploadFormat, useBulkClientUpload } from './useBulkClientUpload';

const FORMAT_OPTIONS = [{ value: 'ebs', label: 'EBS' }];

type Props = {
  companyId: string;
  opened: boolean;
  onClose: () => void;
};

export default function BulkUploadModal({ companyId, opened, onClose }: Props) {
  const [format, setFormat] = useState<BulkUploadFormat>('ebs');
  const [file, setFile] = useState<File | null>(null);
  const { uploadClients, isPending, result, errorMessage, progress, reset } =
    useBulkClientUpload(companyId);

  const handleClose = () => {
    setFile(null);
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (!file) return;
    try {
      await uploadClients({ format, file });
      setFile(null);
    } catch (e) {
      notifyAndLogError('Bulk client upload failed', 'BulkUploadModal', e);
    }
  };

  return (
    <Modal opened={opened} onClose={handleClose} title="Bulk upload clients" centered>
      <ModalContent
        errorMessage={errorMessage}
        result={result}
        format={format}
        setFormat={setFormat}
        file={file}
        setFile={setFile}
        progress={progress}
        isPending={isPending}
        handleClose={handleClose}
        handleUpload={handleUpload}
      />
    </Modal>
  );
}

type ModalContentProps = {
  errorMessage: string | null;
  result: { clientsCreated: number; familiesCreated: number; skipped: string[] } | null;
  format: BulkUploadFormat;
  setFormat: (format: BulkUploadFormat) => void;
  file: File | null;
  setFile: (file: File | null) => void;
  progress: number | null;
  isPending: boolean;
  handleClose: () => void;
  handleUpload: () => void;
};

function ModalContent({
  errorMessage,
  result,
  format,
  setFormat,
  file,
  setFile,
  progress,
  isPending,
  handleClose,
  handleUpload,
}: ModalContentProps) {
  if (errorMessage) {
    return (
      <Flex direction="column" gap="md">
        <Alert color="red" title="Upload failed">
          {errorMessage}
        </Alert>
        <Group justify="flex-end">
          <Button onClick={handleClose}>Done</Button>
        </Group>
      </Flex>
    );
  }

  if (result) {
    return (
      <Flex direction="column" gap="md">
        <Text>
          Created <strong>{result.clientsCreated}</strong> clients across{' '}
          <strong>{result.familiesCreated}</strong> families.
        </Text>
        {result.skipped.length > 0 && (
          <>
            <Text c="dimmed" size="sm">
              {result.skipped.length} row(s) skipped:
            </Text>
            <List size="sm" c="dimmed">
              {result.skipped.map((reason, i) => (
                <List.Item key={i}>{reason}</List.Item>
              ))}
            </List>
          </>
        )}
        <Group justify="flex-end">
          <Button onClick={handleClose}>Done</Button>
        </Group>
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="md">
      <Select
        label="Format"
        data={FORMAT_OPTIONS}
        value={format}
        onChange={v => v && setFormat(v as BulkUploadFormat)}
      />
      <FileInput
        label="Spreadsheet"
        accept=".csv"
        value={file}
        onChange={setFile}
        leftSection={<IconUpload size={16} />}
        placeholder="Choose CSV file"
      />
      {progress != null && (
        <Group gap="xs" mt="sm" align="center">
          <Progress value={progress} style={{ flex: 1 }} />
          <Text size="sm" c="dimmed">
            {progress}%
          </Text>
        </Group>
      )}
      <Group justify="flex-end" mt="sm">
        <Button variant="subtle" onClick={handleClose} disabled={isPending}>
          Cancel
        </Button>
        <Button onClick={handleUpload} disabled={!file} loading={isPending}>
          Upload
        </Button>
      </Group>
    </Flex>
  );
}
